//libraries
import React, { Suspense } from 'react';
import styled from 'styled-components';
import { HashLink } from 'react-router-hash-link';

// styles
import { Frame, PrimaryButton, Flex, ColumnedFlex, Link } from 'shared/styledComponents';
import { Text, Eyebrow, H2, H3 } from 'shared/typography';

// hooks
import useResize from 'hooks/useResize';

// styled components
import { JoinCommunityButton } from './RealStoriesSearch/RealStoriesSearch';

// theme
import devices from 'theme/devices';

// utils
import parseHtml from 'utils/parseHtmlToReact';

// custom components
import Layout from 'components/Layout';
import Icon from 'components/Icon';

const RelatedStories = React.lazy(() => import('./RelatedStories'));

const TopBlock = styled.div`
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  padding: 80px 0;
`;

const MainSection = styled(Flex)<any>`
  width: 100%;
  align-items: flex-start;
  justify-content: center;

  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

const StyledIcon = styled(Icon)`
  justify-content: center;
  position: relative;
  top: 65px;
  left: 35px;
  z-index: 1;
`;

const ImageBlock = styled(ColumnedFlex)`
  position: relative;
  top: -370px;

  @media ${devices.mobile} {
    position: unset;
    top: unset;
    margin-bottom: 50px;
    order: 2;
  }
`;

const ImageWrapper = styled.div<any>`
  width: 500px;
  height: 540px;
  background-image: ${({ src }): string => `url(${src})`};
  background-position: center;
  background-size: cover;
  background-position-y: 0;
  background-repeat: no-repeat;
  cursor: pointer;
  margin-bottom: 105px;

  @media ${devices.mobile} {
    width: 310px;
    height: 335px;
    margin-bottom: 30px;
  }
`;

const Quote = styled(H3)`
  font-family: ${({ theme: { fonts } }): string => fonts.canelaThinItalic};
  max-width: 400px;
  padding: 5px;
`;

const AuthorBlock = styled(ColumnedFlex)`
  width: 100%;
  max-width: 450px;
  min-height: 495px;
  position: relative;
  top: -65px;
  align-items: center;
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  padding: 100px 90px 30px;

  @media ${devices.mobile} {
    position: unset;
    top: unset;
    min-height: unset;
    background-color: transparent;
    padding: 50px 20px 0px;
    margin-bottom: 30px;
    order: 1;
  }
`;

const AuthorBlockHeading = styled(Eyebrow)`
  margin-bottom: 20px;
`;

const AuthorName = styled(H2)`
  font-size: 54px;
  text-align: center;
  margin-bottom: 25px;

  @media ${devices.mobile} {
    font-size: 54px;
    line-height: 65px;
  }
`;

const AuthorDescription = styled(Text)`
  text-align: center;
  margin-bottom: 30px;
`;

const SocialLink = styled(Link)``;

const BodySection = styled(ColumnedFlex)`
  align-items: center;

  @media ${devices.mobile} {
    margin-bottom: 80px;
  }
`;

const Body = styled(ColumnedFlex)`
  position: relative;
  top: -210px;
  max-width: 730px;

  @media ${devices.mobile} {
    position: unset;
    top: unset;
  }
`;

// const ShareBlock = styled(ColumnedFlex)``;

// const ShareHeading = styled(Eyebrow)`
//   margin-bottom: 20px;
// `;

// const SocialIcons = styled(Flex)`
//   width: 100%;
//   max-width: 100px;
//   justify-content: space-between;
// `;

// const SocialIcon = styled(Icon)<any>``;

const RelatedStoriesSection = styled(ColumnedFlex)<any>`
  width: 100%;
  position: relative;
  top: -140px;
  align-items: flex-start;

  @media ${devices.mobile} {
    position: unset;
    top: unset;
    margin-bottom: 130px;
  }
`;

const RelatedStoriesHeading = styled(H3)`
  margin-bottom: 35px;

  @media ${devices.mobile} {
    margin-bottom: 25px;
  }
`;

const ContactSection = styled(Flex)<any>`
  justify-content: center;
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  padding: 100px 0;

  @media ${devices.mobile} {
    padding: 50px 0;
  }
`;

const ContactSectionContent = styled(ColumnedFlex)<any>`
  align-items: center;
  max-width: 480px;

  @media ${devices.mobile} {
    max-width: 210px;
  }
`;

const ContactSectionHeading = styled(H2)<any>`
  text-align: center;
  margin-bottom: 60px;
`;

const ContactSectionButtons = styled(Flex)<any>`
  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

interface RealStoryProps {
  storyInfo: any;
  stories: any[];
  onStoryClick: (url: string) => () => void;
  onCreateStoryClick: () => void;
}

const RealStory = ({
  storyInfo: { author, subtitle, featuredQuote, photoUrl, instagramHandle, body },
  stories,
  onStoryClick,
  onCreateStoryClick,
}: RealStoryProps): JSX.Element => {
  const { isDesktop } = useResize();

  return (
    <Layout>
      {isDesktop && <TopBlock />}
      <Frame>
        {isDesktop && <StyledIcon type="realStory" width="230px" height="265px" />}
        <MainSection as="section">
          <ImageBlock>
            <ImageWrapper src={photoUrl}/>
            <Quote as="q">{featuredQuote}</Quote>
          </ImageBlock>
          <AuthorBlock>
            <AuthorBlockHeading>journey</AuthorBlockHeading>
            <AuthorName>{author}</AuthorName>
            <AuthorDescription>{subtitle}</AuthorDescription>
            <SocialLink
              href={`https://www.instagram.com/${!!instagramHandle && instagramHandle.substr(1)}`}
              target="_blank"
              size="small"
            >
              {instagramHandle}
            </SocialLink>
          </AuthorBlock>
        </MainSection>
        <BodySection as="section">
          <Body>{parseHtml(body)}</Body>
        </BodySection>
        {/* <ShareBlock>
              <ShareHeading>Share</ShareHeading>
              <SocialIcons>
                <SocialIcon type="facebook" />
                <SocialIcon type="twitter" />
                <SocialIcon type="mail" />
              </SocialIcons>
            </ShareBlock> */}
        {stories && !!stories.length && (
          <RelatedStoriesSection as="section">
            <RelatedStoriesHeading>Related Journeys</RelatedStoriesHeading>
            <Suspense fallback={<div>Loading...</div>}>
              <RelatedStories stories={stories} onStoryClick={onStoryClick} />
            </Suspense>
          </RelatedStoriesSection>
        )}
        <ContactSection as="section">
          <ContactSectionContent>
            <ContactSectionHeading>Help us change the conversation.</ContactSectionHeading>
            <ContactSectionButtons>
              <PrimaryButton size="small" onClick={onCreateStoryClick}>
                share your story
              </PrimaryButton>
              <JoinCommunityButton as={HashLink} to="#listYourPractice" size="small" smooth>
                join our community
              </JoinCommunityButton>
            </ContactSectionButtons>
          </ContactSectionContent>
        </ContactSection>
      </Frame>
    </Layout>
  );
};

export default RealStory;
